import React, { ReactNode, useEffect, useState } from 'react';
import useForm from '@/hooks/useForm';
import { toast } from 'react-toastify';
import api, { AxiosError } from '@/services/api';
import { SectorProductConfigSectorContainer } from '@/features/registerEvent/components/SectorProductConfigSectorSreen/screens/ui';
import { useParams } from 'react-router-dom';
import { useDialog } from '@/hooks/useDialog';
import { DeleteContent } from '@/components/DeleteContent';
import { useConfirmDelete } from '@/hooks/useConfirmDelete';
import Section from '@/model/Section';
import validators from '@/helpers/validators';
import { States } from '@/helpers/common/states';
import EventSection from '@/model/EventSection';
import Product from '@/model/Product';
import Combo from '@/model/Combo';
import CustomError from '@/model/CustomError';
import ProductSectionEvent from '@/model/SectionProductEvent';
import { ShouldShowModal, NameFiles, SectionGroup, SectionSubGroup, FormInputName } from '../types';

type UrlParams = {
  id: string;
};

interface StateProps {
  numberTab: number;
}
interface DispatchProps {
  handleGetEventPhaseCompletion: () => void;
  nextTab: () => void;
  backTab: () => void;
}

type Props = StateProps & DispatchProps;

export const SectorProductConfigSectorScreen: React.FC<Props> = ({
  numberTab,
  backTab,
  nextTab,
}): JSX.Element => {
  const [state, setState] = useState<States>(States.default);
  const [sections, setSections] = useState<Section[]>([]);
  const [formNameFiles, setFormNameFiles] = useState<NameFiles>({});

  const [shouldShowModal, setShouldShowModal] = useState<ShouldShowModal>(
    ShouldShowModal.configProduct,
  );
  const [eventSection, setEventSection] = useState<ProductSectionEvent>();
  const [sectionGroups, setSectionGroups] = useState<SectionGroup[]>([]);
  const [eventSections, setEventSections] = useState<ProductSectionEvent[]>([]);

  const { title, visible, onChangeTitle, onToggle } = useDialog();
  const confirmDelete = useConfirmDelete();

  const params = useParams<UrlParams>();

  const handleOnGetSections = async (): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<Section[]>(`/section/find`);
      setSections(data);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const handleGetEventSections = async (id: string): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<ProductSectionEvent[]>(`/event/section-product/${id}/section`);
      setEventSections(data);
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };
  const normalizeUnchecked = (groups: SectionGroup[]): SectionGroup[] =>
    (groups || []).map(g => ({
      ...g,
      subGroups: (g.subGroups || []).map(sg => ({
        ...sg,
        checked: false,
        products: (sg.products || []).map(p => ({ ...p, checked: false })),
        combos: (sg.combos || []).map(c => ({ ...c, checked: false })),
      })),
    }));

  const handleGetProducsAndCombos = async (id: string): Promise<void> => {
    try {
      setState(States.loading);
      const { data } = await api.get<SectionGroup[]>(
        `/event/section-product/${id}/product/section`,
      );
      setSectionGroups(normalizeUnchecked(data));
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    } finally {
      setState(States.default);
    }
  };

  const { formData, formErrors, onChangeFormInput, isFormValid, setErrors, resetForm } = useForm({
    initialData: {
      section: '',
      imageBase64Sector: '',
    },
    validators: {
      section: [validators.required],
    },
  });

  const handleOnSelectAllSubGroups = (
    categoryId: string,
    subCategoryId: string,
    checked: boolean,
  ): void => {
    const newSectionGroups: SectionGroup[] = [];
    if (sectionGroups && sectionGroups.length > 0) {
      sectionGroups.forEach(sectionGroup => {
        if (sectionGroup.categoryGroupId === categoryId) {
          const newSectionGroup = {
            ...sectionGroup,
            subGroups: [],
          } as SectionGroup;
          if (sectionGroup.subGroups && sectionGroup.subGroups.length > 0) {
            sectionGroup.subGroups.forEach(subGroup => {
              if (subGroup.categorySubGroupId === subCategoryId) {
                const newSubGroup = {
                  ...subGroup,
                  products: [],
                  combos: [],
                  checked,
                } as SectionSubGroup;
                if (subGroup.products && subGroup.products.length > 0) {
                  subGroup.products.forEach(product => {
                    const newProduct = {
                      ...product,
                      checked,
                    };
                    newSubGroup.products.push(newProduct);
                  });
                }
                if (subGroup.combos && subGroup.combos.length > 0) {
                  subGroup.combos.forEach(combo => {
                    const newCombo = {
                      ...combo,
                      checked,
                    };
                    newSubGroup.combos.push(newCombo);
                  });
                }
                newSectionGroup.subGroups.push(newSubGroup);
              } else {
                newSectionGroup.subGroups.push(subGroup);
              }
            });
          }
          newSectionGroups.push(newSectionGroup);
        } else {
          newSectionGroups.push(sectionGroup);
        }
      });
    }
    setSectionGroups(newSectionGroups);
  };

  const handleOnCheckProduct = (
    categoryId: string,
    subCategoryId: string,
    productId: string,
  ): void => {
    const newSectionGroups: SectionGroup[] = [];
    if (sectionGroups && sectionGroups.length > 0) {
      sectionGroups.forEach(sectionGroup => {
        if (sectionGroup.categoryGroupId === categoryId) {
          const newSectionGroup = {
            ...sectionGroup,
            subGroups: [],
          } as SectionGroup;
          if (sectionGroup.subGroups && sectionGroup.subGroups.length > 0) {
            sectionGroup.subGroups.forEach(subGroup => {
              if (subGroup.categorySubGroupId === subCategoryId) {
                let countCheck = 0;
                const newSubGroup = {
                  ...subGroup,
                  products: [],
                } as SectionSubGroup;
                if (subGroup.products && subGroup.products.length > 0) {
                  subGroup.products.forEach(product => {
                    if (product.id === productId) {
                      const newProduct = {
                        ...product,
                        checked: !product.checked,
                      };
                      newSubGroup.products.push(newProduct);
                      if (newProduct.checked) {
                        countCheck += 1;
                      }
                    } else {
                      newSubGroup.products.push(product);
                      if (product.checked) {
                        countCheck += 1;
                      }
                    }
                  });
                }
                if (newSubGroup.products.length === countCheck) {
                  newSubGroup.checked = true;
                } else {
                  newSubGroup.checked = false;
                }
                newSectionGroup.subGroups.push(newSubGroup);
              } else {
                newSectionGroup.subGroups.push(subGroup);
              }
            });
          }
          newSectionGroups.push(newSectionGroup);
        } else {
          newSectionGroups.push(sectionGroup);
        }
      });
    }
    setSectionGroups(newSectionGroups);
  };

  const handleOnCheckCombo = (categoryId: string, subCategoryId: string, comboId: string): void => {
    const newSectionGroups: SectionGroup[] = [];
    if (sectionGroups && sectionGroups.length > 0) {
      sectionGroups.forEach(sectionGroup => {
        if (sectionGroup.categoryGroupId === categoryId) {
          const newSectionGroup = {
            ...sectionGroup,
            subGroups: [],
          } as SectionGroup;
          if (sectionGroup.subGroups && sectionGroup.subGroups.length > 0) {
            sectionGroup.subGroups.forEach(subGroup => {
              if (subGroup.categorySubGroupId === subCategoryId) {
                let countCheck = 0;
                const newSubGroup = {
                  ...subGroup,
                  combos: [],
                } as SectionSubGroup;
                if (subGroup.combos && subGroup.combos.length > 0) {
                  subGroup.combos.forEach(combo => {
                    if (combo.id === comboId) {
                      const newCombo = {
                        ...combo,
                        checked: !combo.checked,
                      };
                      newSubGroup.combos.push(newCombo);
                      if (newCombo.checked) {
                        countCheck += 1;
                      }
                    } else {
                      newSubGroup.combos.push(combo);
                      if (combo.checked) {
                        countCheck += 1;
                      }
                    }
                  });
                }
                if (newSubGroup.products.length === countCheck) {
                  newSubGroup.checked = true;
                } else {
                  newSubGroup.checked = false;
                }
                newSectionGroup.subGroups.push(newSubGroup);
              } else {
                newSectionGroup.subGroups.push(subGroup);
              }
            });
          }
          newSectionGroups.push(newSectionGroup);
        } else {
          newSectionGroups.push(sectionGroup);
        }
      });
    }
    setSectionGroups(newSectionGroups);
  };

  const handleOnCancelEdit = (): void => {
    try {
      setEventSection(undefined);
      setFormNameFiles({});
      resetForm();
      if (params?.id) {
        handleGetProducsAndCombos(params.id);
      } else {
        setSectionGroups([]);
      }
    } catch (error) {
      const err = error as AxiosError;
      toast.error(err.message);
    }
  };

  const handleOnChangeFileInput =
    (inputName: string) =>
    (file: File | undefined): void => {
      // validate if file is image
      if (file && file.type.match(/image\/(jpg|jpeg|png)/)) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          const base64 = reader.result?.toString();
          if (base64) {
            setFormNameFiles({ ...formNameFiles, [inputName]: file.name });
            onChangeFormInput(inputName)(base64);
          }
        };
      } else {
        setErrors({
          [inputName]: ['O formato deve ser .jpg, .jpeg ou .png'],
        });
      }
    };

  const handleOnSave = async (): Promise<void> => {
    const products: Product[] = [];
    const combos: Combo[] = [];

    if (sectionGroups && sectionGroups.length > 0) {
      sectionGroups.forEach(sectionGroup => {
        if (sectionGroup.subGroups && sectionGroup.subGroups.length > 0) {
          sectionGroup.subGroups.forEach(subGroup => {
            if (subGroup.products && subGroup.products.length > 0) {
              subGroup.products.forEach(product => {
                if (product.checked) {
                  products.push({ id: product.id } as Product);
                }
              });
            }
            if (subGroup.combos && subGroup.combos.length > 0) {
              subGroup.combos.forEach(combo => {
                if (combo.checked) {
                  combos.push({ id: combo.id } as Combo);
                }
              });
            }
          });
        }
      });
    }

    if ((products && products.length > 0) || (combos && combos.length > 0)) {
      try {
        setState(States.loading);
        const payloadSection = {
          id: formData[FormInputName.section],
          imageBase64: formData[FormInputName.imageBase64Sector],
        };

        const { data } = await api.post<Section>(
          `/event/section-product/${params.id}/section`,
          payloadSection,
        );

        const payloadSectionProducts = {
          section: data,
          products,
          combos,
        } as EventSection;

        await api.post(
          `/event/section-product/${params.id}/section/products`,
          payloadSectionProducts,
        );
        toast.success('Dados salvos com sucesso!');

        handleGetEventSections(params.id);
        handleOnCancelEdit();
        resetForm();
        onToggle();
      } catch (error) {
        if (error instanceof AxiosError) {
          const err = error as AxiosError<CustomError>;
          if (err.response && err.response.data) {
            if (err.response.data.details && err.response.data.details.length > 0) {
              err.response.data.details.forEach(value => {
                toast.error(value);
              });
            } else if (err.response.data.message) {
              toast.error(err.response.data.message);
            }
          }
        }
      } finally {
        setState(States.default);
      }
    } else {
      toast.error('Preencha os campos obrigatórios');
    }
  };

  const handleOnGetEventSection = (selected: ProductSectionEvent): void => {
    onChangeFormInput(FormInputName.section)(selected.sectionId);
    onChangeFormInput(FormInputName.imageBase64Sector)(selected.sectionImage);
    setEventSection(selected);
  };

  const loadProductsAndCombosFromSelected = (eventSectionSelected: ProductSectionEvent): void => {
    if (sectionGroups && sectionGroups.length > 0) {
      const newSectionGroups: SectionGroup[] = [];
      sectionGroups.forEach(group => {
        let foundGroup = false;
        if (eventSectionSelected.sectionGroup && eventSectionSelected.sectionGroup.length > 0) {
          // eslint-disable-next-line no-plusplus
          for (let g = 0; g < eventSectionSelected.sectionGroup.length; g++) {
            if (group.categoryGroupId === eventSectionSelected.sectionGroup[g].categoryGroupId) {
              foundGroup = true;
              const newSectionGroup: SectionGroup = {
                ...group,
                subGroups: [],
              };
              if (group.subGroups && group.subGroups.length > 0) {
                group.subGroups.forEach(subGroup => {
                  let foundSubGroup = false;
                  if (
                    eventSectionSelected.sectionGroup[g].subGroups &&
                    eventSectionSelected.sectionGroup[g].subGroups.length > 0
                  ) {
                    for (
                      let s = 0;
                      s < eventSectionSelected.sectionGroup[g].subGroups.length;
                      // eslint-disable-next-line no-plusplus
                      s++
                    ) {
                      if (
                        subGroup.categorySubGroupId ===
                        eventSectionSelected.sectionGroup[g].subGroups[s].categorySubGroupId
                      ) {
                        foundSubGroup = true;
                        const newSubGroup: SectionSubGroup = {
                          ...subGroup,
                          products: [],
                          combos: [],
                        };
                        if (subGroup.products && subGroup.products.length > 0) {
                          subGroup.products.forEach(product => {
                            let foundProduct = false;
                            if (
                              eventSectionSelected.sectionGroup[g].subGroups[s].products &&
                              eventSectionSelected.sectionGroup[g].subGroups[s].products.length > 0
                            ) {
                              for (
                                let p = 0;
                                p <
                                eventSectionSelected.sectionGroup[g].subGroups[s].products.length;
                                // eslint-disable-next-line no-plusplus
                                p++
                              ) {
                                if (
                                  product.id ===
                                  eventSectionSelected.sectionGroup[g].subGroups[s].products[p].id
                                ) {
                                  foundProduct = true;
                                  const newProduct = {
                                    ...product,
                                    checked: true,
                                  };
                                  newSubGroup.products.push(newProduct);
                                  break;
                                }
                              }
                            }
                            if (!foundProduct) {
                              newSubGroup.products.push({ ...product, checked: false });
                            }
                          });
                        }
                        if (subGroup.combos && subGroup.combos.length > 0) {
                          subGroup.combos.forEach(combo => {
                            let foundCombo = false;
                            if (
                              eventSectionSelected.sectionGroup[g].subGroups[s].combos &&
                              eventSectionSelected.sectionGroup[g].subGroups[s].combos.length > 0
                            ) {
                              for (
                                let p = 0;
                                p < eventSectionSelected.sectionGroup[g].subGroups[s].combos.length;
                                // eslint-disable-next-line no-plusplus
                                p++
                              ) {
                                if (
                                  combo.id ===
                                  eventSectionSelected.sectionGroup[g].subGroups[s].combos[p].id
                                ) {
                                  foundCombo = true;
                                  const newCombo = {
                                    ...combo,
                                    checked: true,
                                  };
                                  newSubGroup.combos.push(newCombo);
                                  break;
                                }
                              }
                            }
                            if (!foundCombo) {
                              newSubGroup.combos.push({ ...combo, checked: false });
                            }
                          });
                        }
                        newSectionGroup.subGroups.push(newSubGroup);
                        break;
                      }
                    }
                  }
                  if (!foundSubGroup) {
                    newSectionGroup.subGroups.push({ ...subGroup, checked: false });
                  }
                });
              }
              newSectionGroups.push(newSectionGroup);
              break;
            }
          }
        }
        if (!foundGroup) {
          newSectionGroups.push(group);
        }
      });
      if (newSectionGroups && newSectionGroups.length > 0) {
        // eslint-disable-next-line no-plusplus
        for (let g = 0; g < newSectionGroups.length; g++) {
          if (newSectionGroups[g].subGroups && newSectionGroups[g].subGroups.length > 0) {
            // eslint-disable-next-line no-plusplus
            for (let s = 0; s < newSectionGroups[g].subGroups.length; s++) {
              let countProducts = 0;
              let countCombos = 0;
              if (
                newSectionGroups[g].subGroups[s].products &&
                newSectionGroups[g].subGroups[s].products.length > 0
              ) {
                newSectionGroups[g].subGroups[s].products.forEach(data => {
                  if (data.checked) {
                    // eslint-disable-next-line no-plusplus
                    countProducts++;
                  }
                });
              }
              if (
                newSectionGroups[g].subGroups[s].combos &&
                newSectionGroups[g].subGroups[s].combos.length > 0
              ) {
                newSectionGroups[g].subGroups[s].combos.forEach(data => {
                  if (data.checked) {
                    // eslint-disable-next-line no-plusplus
                    countCombos++;
                  }
                });
              }
              let total = 0;
              total +=
                newSectionGroups[g].subGroups[s].products &&
                newSectionGroups[g].subGroups[s].products.length > 0
                  ? newSectionGroups[g].subGroups[s].products.length
                  : 0;
              total +=
                newSectionGroups[g].subGroups[s].combos &&
                newSectionGroups[g].subGroups[s].combos.length > 0
                  ? newSectionGroups[g].subGroups[s].combos.length
                  : 0;
              if (total > 0 && total === countProducts + countCombos) {
                newSectionGroups[g].subGroups[s].checked = true;
              } else {
                newSectionGroups[g].subGroups[s].checked = false;
              }
            }
          }
        }
      }
      setSectionGroups(newSectionGroups);
    }
  };

  const handleOnShouldShowModal = ({
    value,
    newTitleModal,
    eventSection: eventSectionSelected,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | ReactNode;
    eventSection?: ProductSectionEvent;
  }): void => {
    setShouldShowModal(value);
    onChangeTitle(newTitleModal);
    onToggle();
    if (value === ShouldShowModal.configProduct) {
      if (eventSectionSelected) {
        onChangeFormInput(FormInputName.section)(eventSectionSelected.sectionId);
        onChangeFormInput(FormInputName.imageBase64Sector)(eventSectionSelected.sectionImage);
        setEventSection(eventSectionSelected);
        loadProductsAndCombosFromSelected(eventSectionSelected);
      }
    }
  };

  const handleOnConfirmDelete = async (selected: ProductSectionEvent): Promise<void> => {
    try {
      setState(States.loading);
      await api.delete(`/event/section-product/${params?.id}/section/${selected.sectionId}`);
      toast.success('Setor desviculado ao evento');
      handleGetEventSections(params.id);
    } finally {
      confirmDelete.hide();
      setState(States.default);
    }
  };

  const handleOnShowDelete = (selected: ProductSectionEvent): void => {
    confirmDelete.show({
      title: '',
      children: <DeleteContent />,
      actions: [
        {
          title: 'Não, quero manter',
          theme: 'noneBorder',
          onClick: (): void => confirmDelete.hide(),
        },
        {
          title: 'Sim, quero excluir',
          onClick: (): Promise<void> => handleOnConfirmDelete(selected),
        },
      ],
    });
  };

  useEffect(() => {
    if (!visible) {
      handleOnCancelEdit();
    }
  }, [visible]);

  useEffect(() => {
    if (numberTab === 3) {
      handleOnGetSections();
      handleGetEventSections(params.id);
      handleGetProducsAndCombos(params.id);
    }
  }, [numberTab]);

  return (
    <SectorProductConfigSectorContainer
      state={state}
      title={title}
      visible={visible}
      onToggle={onToggle}
      onShouldShowModal={handleOnShouldShowModal}
      sectionGroups={sectionGroups}
      onSelectAllSubGroups={handleOnSelectAllSubGroups}
      onCheckProduct={handleOnCheckProduct}
      onCheckCombo={handleOnCheckCombo}
      sections={sections}
      formData={formData}
      formErrors={formErrors}
      onChangeFormInput={onChangeFormInput}
      onChangeFormFileInput={handleOnChangeFileInput}
      formNameFiles={formNameFiles}
      onGetAllSections={handleOnGetSections}
      onCancelEdit={handleOnCancelEdit}
      shouldShowModal={shouldShowModal}
      eventSection={eventSection}
      isFormValid={isFormValid}
      onSave={handleOnSave}
      eventSections={eventSections}
      onGet={handleOnGetEventSection}
      onShowModalDelete={handleOnShowDelete}
      onReturnTab={backTab}
      onNextTab={nextTab}
    />
  );
};
